import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MAX_QUESTION_CHARS } from 'src/app/shared/constants/chat.constants';
import { normalizeChatMessageContent } from 'src/app/shared/utils/chat-message-validation.util';

const MAX_TEXTAREA_HEIGHT_PX = 100;

@Component({
  standalone: false,
  selector: 'app-chat-composer',
  templateUrl: './chat-composer.component.html',
  styleUrls: ['./chat-composer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComposerComponent {
  @Input() disabled = false;
  /** Separately gates report generation so a stopped-but-still-running ask can free up plain typing/send
   * again while report generation - a rewind-style flow - stays blocked until that turn actually settles. */
  @Input() reportDisabled = false;
  @Output() send = new EventEmitter<string>();
  @Output() generateReport = new EventEmitter<string>();

  @ViewChild('textareaRef') textareaRef!: ElementRef<HTMLTextAreaElement>;

  readonly maxLength = MAX_QUESTION_CHARS;

  hasContent = false;
  charCount = 0;

  focusInput(): void {
    if (this.disabled) {
      return;
    }
    this.textareaRef.nativeElement.focus();
  }

  onInput(): void {
    const value = this.textareaRef.nativeElement.value;
    this.hasContent = value.trim().length > 0;
    this.charCount = value.length;
    this.autogrow();
  }

  restoreContent(text: string): void {
    this.textareaRef.nativeElement.value = text;
    this.hasContent = text.trim().length > 0;
    this.charCount = text.length;
    this.autogrow();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSendClick();
    }
  }

  onSendClick(): void {
    const value = this.takeContent();
    if (value === null) {
      return;
    }
    this.send.emit(value);
  }

  onGenerateReportClick(): void {
    if (this.reportDisabled) {
      return;
    }
    const value = this.takeContent();
    if (value === null) {
      return;
    }
    this.generateReport.emit(value);
  }

  private takeContent(): string | null {
    if (this.disabled) {
      return null;
    }
    const value = normalizeChatMessageContent(this.textareaRef.nativeElement.value, this.maxLength);
    if (value === null) {
      return null;
    }

    this.textareaRef.nativeElement.value = '';
    this.hasContent = false;
    this.charCount = 0;
    this.resetHeight();
    return value;
  }

  private autogrow(): void {
    const element = this.textareaRef.nativeElement;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }

  private resetHeight(): void {
    this.textareaRef.nativeElement.style.height = 'auto';
  }
}
